import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  IconButton,
  Alert,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import { usePhone } from '../../contexts/PhoneContext';
import { useCallStore } from '../../stores/root_store';
import type { Call } from '../../api/call_api';
import { incomingPhoneNumberApi, type IncomingPhoneNumber } from '../../api/incoming_phone_number_api';

interface PhoneCallsCardProps {
  requestUuid: string;
  onOpenDialer: (number?: string) => void;
  canWork: boolean;
}

const formatDuration = (seconds: number): string => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatPhoneNumber = (number: string): string => {
  // Simple US phone formatting
  const cleaned = number.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return number;
};

const getStatusColor = (
  status: string
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'in-progress':
    case 'ringing':
      return 'primary';
    case 'failed':
    case 'busy':
    case 'no-answer':
      return 'error';
    default:
      return 'default';
  }
};

export const PhoneCallsCard = observer(function PhoneCallsCard({
  requestUuid,
  onOpenDialer,
  canWork,
}: PhoneCallsCardProps) {
  const {
    phoneReady,
    activeCallKey,
    getCallsByAssociation,
    fetchCallsByAssociation,
    fetchCallParticipants,
  } = usePhone();

  const callStore = useCallStore();

  const [loading, setLoading] = useState(false);
  const [expandedCall, setExpandedCall] = useState<string | false>(false);
  const [callbackNumber, setCallbackNumber] = useState<IncomingPhoneNumber | null>(null);
  const [callbackLoading, setCallbackLoading] = useState(false);
  const [callbackError, setCallbackError] = useState<string | null>(null);

  const calls = getCallsByAssociation('request', requestUuid);

  useEffect(() => {
    const loadCalls = async () => {
      if (requestUuid) {
        setLoading(true);
        await fetchCallsByAssociation('request', requestUuid);
        setLoading(false);
      }
    };
    loadCalls();
  }, [requestUuid, fetchCallsByAssociation]);

  // Auto-fetch existing callback number on mount so it shows immediately
  useEffect(() => {
    if (canWork && requestUuid) {
      incomingPhoneNumberApi.get('request', requestUuid).then(
        (ipn) => setCallbackNumber(ipn),
        () => {} // 404 = none exists yet, user can click the button to create
      );
    }
  }, [canWork, requestUuid]);

  const handleAccordionChange =
    (call: Call) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCall(isExpanded ? call.key : false);
      if (isExpanded && !call.is_legacy) {
        fetchCallParticipants(call.key);
      }
    };

  const handleGetCallbackNumber = async () => {
    setCallbackLoading(true);
    setCallbackError(null);
    try {
      const ipn = await incomingPhoneNumberApi.getOrCreate('request', requestUuid);
      setCallbackNumber(ipn);
    } catch {
      setCallbackError('Failed to get callback number');
    } finally {
      setCallbackLoading(false);
    }
  };

  const handleCopyCallback = () => {
    if (!callbackNumber) return;
    const text = `${callbackNumber.formatted_phone_number} ext. ${callbackNumber.extension}`;
    navigator.clipboard.writeText(text);
  };

  const renderCall = (call: Call) => {
    const isOutgoing = !!call.number_to;
    const participants = callStore.getParticipants(call.key);

    return (
      <Accordion
        key={call.key}
        expanded={expandedCall === call.key}
        onChange={handleAccordionChange(call)}
        disableGutters
        elevation={0}
        sx={{
          '&:before': { display: 'none' },
          bgcolor: call.key === activeCallKey ? 'success.50' : 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 1, minHeight: 48 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            {isOutgoing ? (
              <CallMadeIcon fontSize="small" color="primary" />
            ) : (
              <CallReceivedIcon fontSize="small" color="secondary" />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                noWrap
                onClick={(e) => {
                  const num = call.number_to || call.number_from;
                  if (num && canWork) {
                    e.stopPropagation();
                    onOpenDialer(num);
                  }
                }}
                sx={canWork ? {
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline', color: 'primary.main' },
                } : undefined}
              >
                {formatPhoneNumber(call.number_to || call.number_from || 'Unknown')}
                {participants.length > 1 && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}+{participants.length - 1}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(call.date_created).toLocaleString()}
                {call.is_legacy && ' · old site'}
              </Typography>
            </Box>
            <Chip
              label={call.twilio_status || 'pending'}
              size="small"
              color={getStatusColor(call.twilio_status)}
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, py: 0, pb: 1 }}>
          {/* Call duration */}
          <Typography variant="caption" color="text.secondary">
            Duration: {formatDuration(call.twilio_duration)}
          </Typography>

          {/* Participants */}
          {participants.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Participants
              </Typography>
              {participants.map((p) => (
                <Box
                  key={p.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    onClick={(e) => {
                      if (p.phone_number && canWork) {
                        e.stopPropagation();
                        onOpenDialer(p.phone_number);
                      }
                    }}
                    sx={canWork ? {
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline', color: 'primary.main' },
                    } : undefined}
                  >
                    {formatPhoneNumber(p.phone_number)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!!p.twilio_duration && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDuration(p.twilio_duration)}
                      </Typography>
                    )}
                    <Chip
                      label={p.twilio_status || 'pending'}
                      size="small"
                      color={getStatusColor(p.twilio_status)}
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon color="primary" />
            <Typography variant="h6">Phone Calls</Typography>
            {loading && <CircularProgress size={16} />}
          </Box>
        </Box>

        {/* Make Call Button */}
        {canWork && (
          <Button
            variant="outlined"
            startIcon={<PhoneIcon />}
            onClick={() => onOpenDialer()}
            disabled={!phoneReady}
            fullWidth
            sx={{ mb: 2 }}
          >
            {activeCallKey ? 'Active Call' : 'Make Call'}
          </Button>
        )}

        {!phoneReady && canWork && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, textAlign: 'center' }}>
            Phone system initializing...
          </Typography>
        )}

        {/* Callback Number */}
        {canWork && !callbackNumber && (
          <Button
            variant="text"
            size="small"
            startIcon={callbackLoading ? <CircularProgress size={14} /> : <PhoneCallbackIcon />}
            onClick={handleGetCallbackNumber}
            disabled={callbackLoading}
            fullWidth
            sx={{ mb: 2 }}
          >
            Get Callback Number
          </Button>
        )}
        {callbackError && (
          <Alert severity="error" sx={{ mb: 2 }}>{callbackError}</Alert>
        )}
        {callbackNumber && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Callback Number
              </Typography>
              <Typography variant="body2">
                {callbackNumber.formatted_phone_number} ext. {callbackNumber.extension}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCopyCallback} title="Copy to clipboard">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Call History */}
        {calls.length > 0 ? (
          <Box sx={{ mx: -1 }}>{calls.map(renderCall)}</Box>
        ) : !loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            No calls yet
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
});
